import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { GlobalStorageService } from '@df/browser/storage/global-storage.service';
import { App } from '@df/core/app';
import { CookiesService } from '@df/core/service/cookies';
import { EAsyncPriority, TimeoutService } from '@df/ng/timeout.service';
import each from 'lodash-es/each';
import find from 'lodash-es/find';
import isEqual from 'lodash-es/isEqual';
import pick from 'lodash-es/pick';
import { BehaviorSubject } from 'rxjs';

/**
 * Token interface used with setter.
 *
 * @export
 * @interface IAuthToken
 */
export interface IAuthToken {
    // token hash
    token: string;
    // valid till timestamp (js one)
    valid: number;
}

/**
 * Areas permisions.
 *
 * @export
 * @interface IAuthAcl
 */
export interface IAuthAcl {
    // Admin panel
    core: boolean;
    // Developer's tools
    dev: boolean;
    // iCMS
    cms: boolean;
    // iCMS editing
    cms_edit: boolean;
    // iCMS publishing
    cms_publish: boolean;
    // iCMS media management
    cms_media: boolean;
    // full components styling
    cms_components: boolean;
    // iCMS reindex
    cms_reindex: boolean;
}

/**
 * Complete autorisation setup.
 *
 * @export
 * @interface IAuthStatus
 */
export interface IAuthStatus {
    // permissions
    acl: IAuthAcl;
    // user name
    name: string;
    // role name
    role: string;
    // list of stores
    stores?: number[];
}

export interface ICreateTokenResponse {
    // token data
    token: IAuthToken;
}
export type TAclRuleAccessLevel = 'dev' | 'prerender';
export type TAclRule = true | false | TAclRuleAccessLevel | TAclRuleAccessLevel[];
export interface IAclRules {
    [id: string]: TAclRule;
}

const authStatusStorage = '_authStatus';

/**
 * Access control.
 *
 * @export
 * @class Acl
 */
@Injectable({
    providedIn: 'root'
})
export class Acl {
    /**
     * ACL rules set
     */
    static rules: IAclRules = {};

    /**
     * Initially loaded status (from local storage)
     * Later on token may change in it (if we have new one)
     * But if ACL will try to change we will just reload page
     */
    readonly authStatus = new BehaviorSubject(this.getAuthStatusFromStorage());

    /**
     * If its production env.
     *
     * @type {boolean}
     */
    isProduction: boolean = this.app.dfConfig.environment === 'production' || this.app.dfConfig.environment === 'uat';

    /**
     * If developer mode is on (by ACL or cookie).
     *
     * @type {boolean}
     */
    private _isDeveloper?: boolean;

    get isDeveloper(): boolean {
        if (this._isDeveloper === undefined) {
            this._isDeveloper = this.app.isPlatformBrowser && this.document.cookie.match(/isDeveloper/) ? true : false;
        }
        return this._isDeveloper;
    }

    /**
     * If this is admin app
     */
    isAdminApp = false;

    /**
     * Token recheck timeout
     */
    tokenCheckTimeout?: number;

    /**
     * Token data
     */
    private tokenData: IAuthToken | undefined;

    /**
     * Token load promise
     */
    private tokenPromise?: Promise<ICreateTokenResponse>;

    /**
     * Creates an instance of Acl.
     *
     * @memberof Acl
     */
    constructor(
        private app: App,
        @Inject(DOCUMENT) private document: Document,
        private requestService: RequestService,
        private globalStorageService: GlobalStorageService,
        private timeoutService: TimeoutService,
        private cookieService: CookiesService
    ) {
        app.acl = this;

        if (app.isPlatformBrowser) {
            this.timeoutService.async(() => {
                this.authStatus.subscribe(status => this.setAuthStatusInStorage(status));
                this.load();
            }, EAsyncPriority.bootstrap);
        }
        each(app.dfConfig.ms.sites, (site, siteId) => {
            if (site.acl !== undefined) {
                Acl.rules['store.' + siteId] = site.acl;
            }
        });
    }

    static isProdMode(): boolean {
        if (typeof window !== 'undefined') {
            if (!['production', 'uat'].includes(window.dfConfig.environment)) {
                return false;
            }
            if (document.cookie.match(/isDeveloper/)) {
                return false;
            }
        }
        return true;
    }

    get cmsStores(): number[] {
        return this.authStatus.value?.stores || this.app.multisite.all.map(i => i.id);
    }

    /**
     * Checks if user has access to given asset
     * @param asset
     * @param defaultToOpen - if set then we will assume that access is granted when rule is not defined
     */
    check(asset: keyof IAuthAcl | string, defaultToOpen = false): boolean {
        if (Object.hasOwnProperty.call(Acl.rules, asset)) {
            const rule = Acl.rules[asset];
            if (typeof rule === 'boolean') {
                return rule;
            }

            return !!find(Array.isArray(rule) ? rule : [rule], level => {
                return (
                    (this.isDeveloper && level === 'dev') ||
                    (level === 'prerender' && ['prerender', 'legacyPrerender'].includes(this.app.client.mode))
                );
            });
        } else if (defaultToOpen) {
            return true;
        }
        const status = this.authStatus.value;
        if (!status?.acl?.[asset]) {
            return false;
        }
        if (status.stores && !status.stores.includes(this.app.multisite.storeId) && asset.match(/cms_/)) {
            return false;
        }
        return true;
    }

    /**
     * Sync ACL with BE
     */
    load() {
        const fake = this.app.localStorageService.get('fakeAuthLoad') as IAuthStatus;
        if (fake) {
            this.setAuthStatus(fake);
            return;
        }

        this.requestService.send(
            undefined,
            { path: this.app.dfConfig.endpoint.mage.replace(/\/$/, '') + '/auth/load' },
            (result: IAuthStatus | undefined) => {
                this.setAuthStatus(result);
            },
            () => this.setAuthStatus(),
            { method: 'GET', withCredentials: true }
        );
    }

    /**
     * Generates token granting access to iCMS BE features
     */
    loadToken() {
        const fake = this.app.localStorageService.get('fakeAuthToken') as ICreateTokenResponse;
        if (fake) {
            this.tokenData = fake.token;
            this.tokenPromise = new Promise<ICreateTokenResponse>(r => {
                this.timeoutService.setTimeout(() => {
                    this.tokenPromise = undefined;
                    r(fake);
                }, 100);
            });
            return;
        }

        this.tokenPromise = this.requestService.send(
            undefined,
            { path: this.app.dfConfig.endpoint.mage.replace(/\/$/, '') + '/auth/createToken' },
            (result: ICreateTokenResponse | undefined) => {
                this.tokenData = result?.token;
                this.tokenPromise = undefined;
            },
            () => {
                this.tokenPromise = undefined;
            },
            { method: 'GET', withCredentials: true }
        );
    }

    /**
     * Sets CMS authorisation token.
     *
     * @param {IAuthToken} token
     */
    setAuthStatus(status?: IAuthStatus) {
        const newStatus = status && typeof status === 'object' ? <IAuthStatus>pick(status, this.statusFields()) : undefined;
        const authStatus = this.authStatus.value;
        const oldStatus = authStatus && typeof authStatus === 'object' ? authStatus : undefined;
        const isChanged =
            !!newStatus !== !!oldStatus ||
            (oldStatus && newStatus && !(isEqual(newStatus.acl, oldStatus.acl) && isEqual(newStatus.stores, oldStatus.stores)));

        if (newStatus) {
            this.cookieService.set('universalOff', '1');
        } else {
            if (isChanged) {
                this.cookieService.delete('universalOff');
            }
        }

        if (isChanged) {
            this.authStatus.next(newStatus);
        }
    }

    protected statusFields(): string[] {
        return ['acl', 'name', 'role', 'stores'];
    }

    /**
     * Retrieves CMS authorisation token string.
     *
     * @return {undefined | string}
     */
    private getToken(): undefined | string {
        if (this.tokenData && this.tokenData.valid > Date.now()) {
            return this.tokenData.token;
        }

        return undefined;
    }

    /**
     * Public method retrieving token
     */
    prepareToken(cb: (token: string | undefined) => void) {
        const token = this.getToken();
        if (token) {
            cb(token);
        } else {
            if (!this.tokenPromise) {
                this.loadToken();
            }
            this.tokenPromise!.finally(() => cb(this.getToken()));
        }
    }

    deactivateToken() {
        this.tokenPromise = undefined;
        this.tokenData = undefined;
    }

    /**
     * Makes sure that token is set
     */
    activateToken(): void {
        if (!this.check('core')) {
            return;
        }
        if (!this.tokenCheckTimeout) {
            const time10m = 10 * 60 * 1000;
            if (!this.tokenData || this.tokenData.valid > Date.now() + time10m) {
                this.loadToken();
            }

            this.tokenCheckTimeout = this.timeoutService.setTimeout(() => this.activateToken(), time10m);
        }
    }

    /**
     * Releases token check timeout
     */
    releaseTokenCheck() {
        if (this.tokenCheckTimeout) {
            clearTimeout(this.tokenCheckTimeout);
            this.tokenCheckTimeout = undefined;
        }
    }

    private setAuthStatusInStorage(status: IAuthStatus | undefined) {
        status ? this.globalStorageService.set(authStatusStorage, status) : this.globalStorageService.remove(authStatusStorage);
    }

    private getAuthStatusFromStorage(): IAuthStatus | undefined {
        return this.globalStorageService.get(authStatusStorage);
    }
}

/**
 * Register service in app.
 */
declare module '@df/core/app' {
    interface App {
        acl: Acl;
    }
}
